import React, { useEffect, useMemo, useState } from 'react';
import { FormInstance, List, message, Upload, UploadProps } from 'antd';
import { OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import { useTranslation } from 'react-i18next';
import { OpRadio } from 'components/customAntd/DLS/OpRadio/OpRadio';
import { OpSelect } from 'components/customAntd/DLS/OpSelect/OpSelect';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { beforeUpload, hasPermission, validateFile } from 'utils/utils';
import { OpButton } from 'components/customAntd/DLS/OpButton/OpButton';
import { UploadOutlined } from '@ant-design/icons';

interface CustomizationFormProps {
    form: FormInstance;
    setSelectedLogoFile: (file: File | null) => void;
    emailTemplates: any[];
    emailTemplateAssigned: any;
    uploadedLogo: string | null;
}

const CustomizationForm: React.FC<CustomizationFormProps> = ({ form, setSelectedLogoFile, emailTemplates, emailTemplateAssigned, uploadedLogo }) => {
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const hasInvitationWrite = hasPermission(tokenScopeList, orgId, 'o', 'invitation:w');

    const { t } = useTranslation();
    const customizationType = OpForm.useWatch('customizationType', form) || 'default';
    const isCustomMessageOn = OpForm.useWatch('customMessage', form) || 0;
    const isCustomLogoOn = OpForm.useWatch('customLogo', form) || 0;

    const [isFileDialogOpen, setIsFileDialogOpen] = useState(false);

    useEffect(() => {
        const onFocusBack = () => setIsFileDialogOpen(false);
        window.addEventListener('focus', onFocusBack);
        return () => {
            // Cleans up the event listener when user leaves the page
            window.removeEventListener('focus', onFocusBack);
        }
    }, []);

    const emailTemplateOptions = useMemo(() => {
        return emailTemplates.map((item) => ({
            label: item.name,
            value: item.id.toString(),
        }));
    }, [emailTemplates]);

    useEffect(() => {
        if (isCustomMessageOn === 1) {
            if (emailTemplateAssigned.length > 0) {
                form.setFieldsValue({
                    customizationType: 'template',
                    emailTemplateId: emailTemplateAssigned[0].emailTemplate.id.toString(),
                });
            } else {
                form.setFieldsValue({
                    customizationType: 'default',
                    emailTemplateId: undefined,
                })
            }
        }
    }, [form, emailTemplateAssigned, isCustomMessageOn]);

    const handleFileChange: UploadProps['onChange'] = async (info) => {
        if (info.file.status === "removed") {
            setSelectedLogoFile(null);
            form.setFieldsValue({ logo: null });
            return;
        }

        if (info.fileList[0]) {
            const validFile = await validateFile(info.fileList[0].originFileObj!);
            if (validFile) {
                setSelectedLogoFile(validFile);
                message.success(t('invitations.customization.customLogo.uploadSuccess', {validFileName: validFile.name}));
            }
        }
    };

    return (
        <>
            <List
                header={t('invitations.customization.visitorInvitations.header')}
                bordered
                style={{ marginBottom: 16 }}
            >
                <List.Item style={{ display: 'block' }}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <OpForm.Switch
                            name="customMessage"
                            checked={isCustomMessageOn === 1}
                            onChange={(checked) => form.setFieldsValue({ customMessage: checked ? 1 : 0 })}
                        />
                        <span style={{ marginLeft: 8 }}>{t('invitations.customization.visitorInvitations.customEmail')}</span>
                    </div>

                    {(isCustomMessageOn === 1) && (
                        <>
                            <OpForm.Item name="customizationType" initialValue="default" style={{ marginTop: 16 }}>
                                <OpRadio.Group
                                    options={[
                                        { label: t('invitations.customization.visitorInvitations.defaultEmail'), value: 'default' },
                                        { label: t('invitations.customization.visitorInvitations.customEmail'), value: 'template' },
                                    ]}
                                    disabled={!hasInvitationWrite} />
                            </OpForm.Item>

                            {customizationType === 'default' && (
                                <OpForm.Item name="messageContent" style={{ marginTop: 1 }}>
                                    <OpForm.TextAreaInput
                                        rows={3}
                                        placeholder={t('invitations.form.messageContentPlaceholder')}
                                        maxLength={4000} />
                                </OpForm.Item>
                            )}

                            {customizationType === 'template' && (
                                <OpForm.Item name="emailTemplateId" style={{ marginTop: 1 }}>
                                    <OpSelect
                                        placeholder={t('invitations.customization.visitorInvitations.customEmailPlaceholder')}
                                        options={emailTemplateOptions}
                                        disabled={!hasInvitationWrite} />
                                </OpForm.Item>
                            )}
                        </>
                    )}
                </List.Item>

            </List>

            <List
                header={t('invitations.customization.customLogo.header')}
                bordered
                style={{ marginBottom: 16 }}
            >
                <List.Item style={{ display: 'block' }}>
                    <OpForm.Item
                        style={{ marginBottom: 8 }}
                        extra={
                            <div style={{ marginLeft: 58.2 }}>
                                {t('invitations.customization.customLogo.useCustomLogoDescription')}
                            </div>
                        }
                    >
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <OpForm.Switch
                                name="customLogo"
                                checked={isCustomLogoOn === 1}
                                onChange={(checked) => form.setFieldsValue({ customLogo: checked ? 1 : 0 })}
                            />

                            <span style={{ marginLeft: 8 }}>{t('invitations.customization.customLogo.useCustomLogo')}</span>
                        </div>
                    </OpForm.Item>

                    {(isCustomLogoOn === 1) && (
                        <>
                            {uploadedLogo && !isFileDialogOpen && (
                                <img
                                    src={uploadedLogo}
                                    alt="Uploaded Logo"
                                    style={{ marginLeft: 58.2, marginBottom: 18, maxHeight: 152, maxWidth: 200, borderRadius: 8 }}
                                />
                            )}
                            <OpForm.Item
                                name="logo"
                                valuePropName="fileList"
                                extra={t('invitations.customization.customLogo.uploadDescription')}
                                getValueFromEvent={e => Array.isArray(e) ? e : e?.fileList}
                                style={{ marginLeft: 58.2 }}

                            >
                                <Upload
                                    listType="picture"
                                    maxCount={1}
                                    onChange={handleFileChange}
                                    beforeUpload={beforeUpload(1000000, '1')} // 1,000,000 bytes = 1MB
                                    showUploadList={{ showPreviewIcon: true }}
                                    previewFile={async (file) => {
                                        return URL.createObjectURL(file); // Create a local preview URL
                                    }}>
                                    <OpButton
                                        style={{ marginBottom: 11 }}
                                        icon={<UploadOutlined />}
                                        onClick={() => {
                                            setIsFileDialogOpen(true);
                                        }}
                                        disabled={!hasInvitationWrite}
                                    >
                                        {t('invitations.customization.customLogo.clickToUpload')}
                                    </OpButton>
                                </Upload>
                            </OpForm.Item>
                        </>
                    )}
                </List.Item>
            </List>
        </>
    );
};

export default CustomizationForm;
